import { Plus, X } from 'lucide-react'
import { useEffect, useState, useCallback } from 'react'
import { useProductVariantApi } from 'src/api/db/apiProductVariant'
import { useProductsClosedApi } from 'src/api/db/apiProductsClosed'
import { Column, Row } from 'src/shared/components/Semantic/all'
import { Button } from 'src/shared/components/ui/button'
import { Input } from 'src/shared/components/ui/input'
import { ScrollArea } from 'src/shared/components/ui/scroll-area'

export const VariantsTab = ({
  productToUpdate,
  onHasChangesChange,
  onSaveHandlerReady,
}: {
  productToUpdate: number
  onHasChangesChange?: (hasChanges: boolean) => void
  onSaveHandlerReady?: (handler: () => void) => void
}) => {
  const {
    productVariants,
    deleteProductVariant,
    createProductVariant,
    updateProductVariant,
  } = useProductVariantApi({
    product_id: productToUpdate,
  })

  const { productsClosed } = useProductsClosedApi({
    product_id: productToUpdate,
  })

  const product_ = productsClosed?.data?.[0]

  const [variants, setVariants] = useState<any[]>([])
  const [originalVariants, setOriginalVariants] = useState<any[]>([])
  const [variantsToDelete, setVariantsToDelete] = useState<number[]>([])

  useEffect(() => {
    if (productVariants?.data) {
      setVariants(productVariants.data)
      setOriginalVariants(productVariants.data)
    }
  }, [productVariants])

  // Track changes - only report after variants are loaded
  useEffect(() => {
    if (!onHasChangesChange || !productVariants?.data) return

    const hasNewVariants = variants.some((v) => !v.id)
    const hasDeletions = variantsToDelete.length > 0
    const hasModifications = variants.some((variant) => {
      if (!variant.id) return false // New variants are handled above
      const original = originalVariants.find((ov) => ov.id === variant.id)
      if (!original) return false
      return (
        original.size !== variant.size ||
        original.item_name !== variant.item_name
      )
    })

    onHasChangesChange(hasNewVariants || hasDeletions || hasModifications)
  }, [variants, originalVariants, variantsToDelete, onHasChangesChange, productVariants?.data])

  const handleSaveVariants = useCallback(async () => {
    // Delete removed variants
    if (variantsToDelete?.length) {
      await Promise.all(
        variantsToDelete?.map((variantId) =>
          deleteProductVariant.mutateAsync(variantId)
        )
      )
      setVariantsToDelete([])
    }

    // Update or create variants
    await Promise.all(
      variants.map(async (variant) => {
        if (variant.id) {
          // Find the original variant to compare
          const originalVariant = originalVariants.find(
            (ov: any) => ov.id === variant.id
          )

          // Check if the variant has changed
          const hasChanged =
            !originalVariant ||
            originalVariant.size !== variant.size ||
            originalVariant.item_name !== variant.item_name

          // Only update if there are changes
          if (hasChanged) {
            await updateProductVariant.mutateAsync({
              id: variant.id,
              product_id: Number(product_?.id),
              size: variant.size || null,
              item_name: variant.item_name || null,
            })
          }
        } else {
          await createProductVariant.mutateAsync({
            size: variant.size || null,
            item_name: variant.item_name || null,
            product_id: Number(product_?.id),
          })
        }
      })
    )

    // Update original variants to match current variants (excluding deleted ones)
    // This marks the current state as the new baseline
    const currentVariantsWithoutDeleted = variants.filter(
      (v) => !variantsToDelete.includes(v.id)
    )
    setOriginalVariants([...currentVariantsWithoutDeleted])
  }, [
    variants,
    variantsToDelete,
    originalVariants,
    product_?.id,
    deleteProductVariant,
    updateProductVariant,
    createProductVariant,
  ])

  useEffect(() => {
    if (onSaveHandlerReady) {
      onSaveHandlerReady(handleSaveVariants)
    }
  }, [onSaveHandlerReady, handleSaveVariants])
  return (
    <ScrollArea className="h-[90vh]">
      <Row className="justify-between items-center mb-4">
        <p className="text-sm font-medium">Product variants</p>
        <Button
          isLoading={
            createProductVariant.isPending || updateProductVariant.isPending
          }
          onClick={handleSaveVariants}
        >
          Save variants
        </Button>
      </Row>

      <Button
        className="w-full mb-4 pr-4"
        onClick={() => {
          setVariants([
            ...variants,
            {
              size: '',
              item_name: '',
              product_id: Number(product_?.id),
            },
          ])
        }}
      >
        <Plus className="h-4 w-4 mr-2" />
        Add variant
      </Button>

      <ScrollArea className="h-[90vh] mt-7">
        <Column className="gap-4 pr-4">
          {variants.map((variant, index) => (
            <Column
              key={variant.id || `new-variant-${index}`}
              className="gap-2 border p-3 rounded-lg bg-white"
            >
              <Row className="items-center justify-between">
                <h4 className="font-medium">
                  {variant.item_name || `Variant ${index + 1}`}
                </h4>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => {
                    if (variant.id) {
                      setVariantsToDelete([...variantsToDelete, variant.id])
                    }
                    setVariants(variants.filter((_, i) => i !== index))
                  }}
                >
                  <X className="h-4 w-4" />
                </Button>
              </Row>

              <Row className="gap-2">
                <Input
                  label="Item name"
                  value={variant.item_name || ''}
                  onChange={(e) => {
                    const newVariants = [...variants]
                    newVariants[index] = {
                      ...variant,
                      item_name: e.target.value,
                    }
                    setVariants(newVariants)
                  }}
                />

                <Input
                  label="Size"
                  value={variant.size || ''}
                  onChange={(e) => {
                    const newVariants = [...variants]
                    newVariants[index] = {
                      ...variant,
                      size: e.target.value,
                    }
                    setVariants(newVariants)
                  }}
                />
              </Row>

              <p className="text-xs text-gray-500 mt-2">
                Note: Colors and images are managed in the Colors tab
              </p>
            </Column>
          ))}
        </Column>
      </ScrollArea>
    </ScrollArea>
  )
}
